import React, { useState } from "react";

import { makePostApiCall, makeGetApiCall } from "../../../shared/makeApiCall";
import DeleteConfirmationForm from "../../../components/forms/DeleteConfirmationForm";
import { SnackbarData, SnackbarType } from "../../../components/Snackbar";
import { add } from "date-fns";

export interface ProductRndEquipment {
  id: number;
  product_id: number;
  equipment_name: string;
  description?: string | null;
}

export function ProductRndEquipmentViewModel(
  logout: () => void,
  productId: number,
  addSnackbar?: any
) {
  const [loading, setLoading] = useState(false);
  const [equipments, setEquipments] = useState<ProductRndEquipment[]>([]);
  const [equipmentOptions, setEquipmentOptions] = useState<any[]>([]);
  const [assignedEquipments, setAssignedEquipments] = useState<any[]>([]);
  const [equipmentForm, setEquipmentForm] = useState({
    name: "",
    description: "",
  });

  const getEquipments = async function (product_id: number) {
    setLoading(true);
    const payload: any = { product_id: product_id };
    const callResult: any = await makePostApiCall(
      `productRndEquipments`,
      payload
    );
    setLoading(false);
    if (callResult.success) {
      setEquipments((callResult.data.equipment as ProductRndEquipment[]) || []);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const createEquipment = async function (values: any) {
    const payload = { ...values };
    const callResult: any = await makePostApiCall(
      "createProductRndEquipment",
      payload
    );
    if (callResult.sucess) {
      const id = `${Date.now()}`;
      addSnackbar(
        new SnackbarData(id, "Equipment Added", SnackbarType.Success)
      );
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const updateEquipment = async function (
    equipment: ProductRndEquipment,
    values: any
  ) {
    const callResult: any = await makePostApiCall(
      `updateProductRndEquipment/${equipment.id}`,
      values
    );
    if (callResult.success) {
      const id = `${Date.now()}`;
      addSnackbar(
        new SnackbarData(id, "Equipment updated", SnackbarType.Success)
      );
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const assignEquipmentToProduct = async function (
    product_id: number,
    equipment_id: number
  ) {
    let payload = {
      product_rnd_equipment_id: equipment_id,
      product_id: product_id,
    };

    const callResult: any = await makePostApiCall(
      `assignEquipmentToProduct`,
      payload
    );

    if (callResult.success) {
      const id = `${Date.now()}`;
      addSnackbar(
        new SnackbarData(
          id,
          "Equipment assigned to product",
          SnackbarType.Success
        )
      );
      return true;
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      return false;
    }
  };

  const fetchAssignedEquipmentToProduct = async function (product_id: number) {
    const callResult: any = await makeGetApiCall(
      `assignedEquipments/${product_id}`
    );

    if (callResult.success) {
      const assigned = callResult.data || [];
      setAssignedEquipments(assigned);
      return assigned;
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      return [];
    }
  };

  const unassignEquipmentFromProduct = async function (
    product_id: number,
    equipment_id: number
  ) {
    let payload = {
      product_rnd_equipment_id: equipment_id,
      product_id: product_id,
    };

    const callResult: any = await makePostApiCall(
      `unassignEquipmentFromProduct`,
      payload
    );
    if (callResult.success) {
      const id = `${Date.now()}`;
      addSnackbar(
        new SnackbarData(
          id,
          "Equipment unassigned from product",
          SnackbarType.Success
        )
      );
      return true;
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      return false;
    }
  };

  const deleteEquipment = async function (id: number) {
    setLoading(true);
    const callResult: any = await makePostApiCall(
      `deleteProductRndEquipment/${id}`,
      {}
    );
    setLoading(false);
    if (callResult.success) {
      setEquipments((s) => s.filter((it) => it.id !== id));
      const sid = `${Date.now()}`;
      addSnackbar(
        new SnackbarData(sid, "Equipment deleted", SnackbarType.Success)
      );
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const onChangeEquipmentField = (field: string, value: string) => {
    setEquipmentForm((s) => ({ ...s, [field]: value }));
  };

  const addEquipment = () => {
    if (!equipmentForm.name.trim()) return;
    const newItem = {
      product_id: productId || 0,
      equipment_name: equipmentForm.name.trim(),
      description: equipmentForm.description.trim(),
    };
    createEquipment(newItem);

    getEquipments(productId);
    setEquipmentForm({ name: "", description: "" });
  };

  const removeEquipment = (id: number | string) => {
    deleteEquipment(Number(id));
  };

  const handleEquipmentChange = async function (newValue: any) {
    const newSelection = newValue || [];
    const previousSelection = assignedEquipments;

    // Find newly added equipment
    const added = newSelection.filter(
      (item: any) =>
        !previousSelection.find((prev: any) => prev.value === item.value)
    );

    // Find removed equipment
    const removed = previousSelection.filter(
      (item: any) =>
        !newSelection.find((newItem: any) => newItem.value === item.value)
    );

    // Optimistically update UI
    setAssignedEquipments(newSelection);

    // Process additions
    for (const item of added) {
      const success = await assignEquipmentToProduct(productId, item.value);
      if (!success) {
        // Revert on failure
        setAssignedEquipments(previousSelection);
        return;
      }
    }

    // Process removals
    for (const item of removed) {
      const success = await unassignEquipmentFromProduct(productId, item.value);
      if (!success) {
        // Revert on failure
        setAssignedEquipments(previousSelection);
        return;
      }
    }
  };

  const fetchEquipmentsForDropdown = async function () {
    const callResult: any = await makeGetApiCall(
      `productRndEquipmentForDropdown`
    );
    if (callResult.success) {
      console.log("dropdown equipments", callResult.data);
      setEquipmentOptions(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  return {
    getEquipments,
    createEquipment,
    updateEquipment,
    deleteEquipment,
    loading,
    equipments,
    removeEquipment,
    addEquipment,
    equipmentForm,
    onChangeEquipmentField,
    fetchEquipmentsForDropdown,
    equipmentOptions,
    assignEquipmentToProduct,
    unassignEquipmentFromProduct,
    fetchAssignedEquipmentToProduct,
    assignedEquipments,
    handleEquipmentChange,
  };
}

export default ProductRndEquipmentViewModel;
